import { Component, inject, signal, WritableSignal } from '@angular/core';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { ButtonCtaComponent } from '@components/button-cta/button-cta.component';
import { CsIconModule } from '@components/icon';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { OnboardingLocationService } from '@services/onboarding/onboarding-location.service';
import { AppConfig } from '../../app/app.config';
import { LocationExampleCardComponent } from './location-example-card.component';

/** Dismiss payload meaning the user answered — granted a location, or explicitly declined via Skip. */
export const LOCATION_ANSWERED = 'ANSWERED';

@Component({
  selector: 'app-onboarding-location-sheet',
  templateUrl: './onboarding-location-sheet.component.html',
  imports: [ButtonCtaComponent, CsIconModule, LocationExampleCardComponent],
})
export class OnboardingLocationSheetComponent {
  private readonly bottomSheetRef: MatBottomSheetRef = inject(MatBottomSheetRef);
  private readonly locationService: OnboardingLocationService = inject(OnboardingLocationService);
  private readonly broadcast: BroadcastService = inject(BroadcastService);
  private readonly conn: ConnectionService = inject(ConnectionService);
  private readonly eventLogger: EventLoggerService = inject(EventLoggerService);
  private readonly appConfig: AppConfig = inject(AppConfig);

  protected readonly loading: WritableSignal<boolean> = signal(false);

  /** Male users lead with the hair example, mirroring the location page. */
  protected readonly showHairFirst: boolean = this.conn.getActingUser()?.get('Gender') === this.appConfig.Shared.Gender.MALE;

  constructor() {
    this.eventLogger.cleverTapEvent('pageOpen', JSON.stringify({ pageName: 'onboarding-location-sheet' }));
  }

  protected async useMyLocation(): Promise<void> {
    if (this.loading()) return;
    this.loading.set(true);
    const granted = await this.locationService.captureLocation();
    this.loading.set(false);
    if (granted) this.bottomSheetRef.dismiss(LOCATION_ANSWERED);
  }

  /** Records the decline so onboarding never asks again; dismisses even if that write fails. */
  protected async skip(): Promise<void> {
    if (this.loading()) return;
    this.loading.set(true);
    try {
      await this.locationService.skipLocation();
    } catch (error) {
      this.broadcast.broadcast('NOTIFY', { message: String(error) });
    } finally {
      this.loading.set(false);
    }
    this.bottomSheetRef.dismiss(LOCATION_ANSWERED);
  }

  /** Records nothing; the empty payload keeps the caller on the concern screen. */
  protected close(): void {
    if (this.loading()) return;
    this.bottomSheetRef.dismiss();
  }

  async handleBackPress(): Promise<boolean> {
    this.close();
    return true;
  }
}
